/**
 * Life4Earth V2 — Combined hook for all 5 Mini-Project sub-entity lists.
 *
 * Stage 3b — one hook that fetches Goals / Tasks / Team / BOM / Enablers
 * for the Progress tab read-only display. Combined-hook simplification:
 * for stage 3b's read-only consumer, fetching all 5 in one go is simpler
 * than wiring 5 separate hooks. Stage 3c may split per-entity if finer
 * cache control becomes warranted.
 *
 * DEV-1075 Phase 7.1: the `goals` slot is now sourced from the unified
 * kpi_config + mission_metric_points tables (via the by-mini-project
 * endpoints) rather than the legacy MiniProjectGoals controller. The
 * return shape is MiniProjectGoalView (a view model derived from the
 * unified data, see miniProjectGoalView.ts) — designed to match the
 * legacy MiniProjectGoal field surface so downstream consumers port
 * with import swaps only. The legacy fetchMiniProjectGoals call path
 * is gone from this hook.
 */

import { useState, useEffect, useCallback, useRef } from "react";
import {
  fetchMiniProjectTasks,
  fetchMiniProjectTeamMembers,
  fetchMiniProjectBomItems,
  fetchMiniProjectEnablerLinks,
  type MiniProjectTask,
  type MiniProjectTeamMember,
  type MiniProjectBomItem,
  type MiniProjectEnablerLink,
} from "./miniProjects";
import { fetchKpiConfigsByMiniProject } from "./kpiConfigs";
import { fetchMissionMetricPointsByMiniProject } from "./missionMetrics";
import {
  kpiConfigsToGoalViews,
  type MiniProjectGoalView,
} from "../Data/MiniProjectConfig/miniProjectGoalView";

export interface UseApiMiniProjectSubEntitiesResult {
  /**
   * Mission goals as derived view models from the unified
   * kpi_config + mission_metric_points data. Shape mirrors the
   * legacy MiniProjectGoal field surface for downstream component
   * compatibility — see miniProjectGoalView.ts for the field list.
   */
  goals: MiniProjectGoalView[];
  tasks: MiniProjectTask[];
  teamMembers: MiniProjectTeamMember[];
  bomItems: MiniProjectBomItem[];
  enablerLinks: MiniProjectEnablerLink[];
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

const empty: Omit<UseApiMiniProjectSubEntitiesResult, "loading" | "error" | "refetch"> = {
  goals: [], tasks: [], teamMembers: [], bomItems: [], enablerLinks: [],
};

export function useApiMiniProjectSubEntities(
  miniProjectGuid: string | null | undefined
): UseApiMiniProjectSubEntitiesResult {
  const mountedRef = useRef(true);
  const [state, setState] = useState<UseApiMiniProjectSubEntitiesResult>({
    ...empty, loading: true, error: null, refetch: () => {},
  });

  const refetch = useCallback(async () => {
    if (!miniProjectGuid) {
      setState({ ...empty, loading: false, error: null, refetch });
      return;
    }
    setState((prev) => ({ ...prev, loading: true, error: null }));
    try {
      // DEV-1075 Phase 7.1: goals derived from unified kpi_config +
      // mission_metric_points (Phase 1+2 schema), not the deprecated
      // MiniProjectGoals controller. The two unified fetches run in
      // parallel with the existing 4 sub-entity fetches.
      const [
        kpiConfigs,
        missionPoints,
        tasks,
        teamMembers,
        bomItems,
        enablerLinks,
      ] = await Promise.all([
        fetchKpiConfigsByMiniProject(miniProjectGuid),
        fetchMissionMetricPointsByMiniProject(miniProjectGuid),
        fetchMiniProjectTasks(miniProjectGuid),
        fetchMiniProjectTeamMembers(miniProjectGuid),
        fetchMiniProjectBomItems(miniProjectGuid),
        fetchMiniProjectEnablerLinks(miniProjectGuid),
      ]);
      if (!mountedRef.current) return;
      const goals = kpiConfigsToGoalViews(kpiConfigs, missionPoints);
      setState({ goals, tasks, teamMembers, bomItems, enablerLinks, loading: false, error: null, refetch });
    } catch (err) {
      if (!mountedRef.current) return;
      setState({
        ...empty, loading: false,
        error: err instanceof Error ? err.message : String(err),
        refetch,
      });
    }
  }, [miniProjectGuid]);

  useEffect(() => {
    mountedRef.current = true;
    refetch();
    return () => { mountedRef.current = false; };
  }, [refetch]);

  return { ...state, refetch };
}
